import { useAnalyticsTracking, useEmitUiEvent } from '@montugroup/data-collection';
import { useCallback, useRef } from 'react';
import { DeviceCheckErrorTrackingContext, DeviceCheckWarning, } from '../../components/VideoConsultation/DeviceReadiness/DeviceReadiness.config';
const MEDIA_EVENT_MAP = {
    mic: {
        event_name: 'consultation_microphone_button_click',
        id: 'video_consultation_mic_btn',
    },
    video: {
        event_name: 'consultation_video_button_click',
        id: 'video_consultation_video_btn',
    },
    blur: {
        event_name: 'consultation_video_blur_button_click',
        id: 'video_consultation_video_blur_btn',
    },
};
export const usePatientTracking = () => {
    const emitEvent = useEmitUiEvent();
    const { uiEvent } = useAnalyticsTracking();
    const contextId = uiEvent?.context_id;
    const trackVideoConsultationLoadingPage = useCallback((contextIdOverride) => {
        emitEvent({
            component_name: 'VideoConsultationLoadingPage',
            event_name: 'video_consultation_loading_page_view',
            id: 'video_consultation_loading_page',
            context_id: contextIdOverride || contextId,
        });
    }, [emitEvent, contextId]);
    // NOTE:
    // trackViewConsultationPermission is NOT wrapped by the analytics/consultation context.
    // so the consultation context will NOT auto-populate `consultation_id` like it does elsewhere.
    // We therefore attach the consultation context manually here as a workaround.
    const trackViewConsultationPermission = useCallback((consultationId) => {
        emitEvent({
            component_name: 'VideoConsultationPermissionsPage',
            event_name: 'video_consultation_permissions_page_view',
            id: 'video_consultation_permissions_page',
            context_id: contextId,
        }, consultationId ? { consultation: { consultation_id: consultationId } } : {});
    }, [emitEvent, contextId]);
    const trackVideoConsultationPreview = useCallback((permissions) => {
        emitEvent({
            component_name: 'VideoConsultationPreviewPage',
            event_name: 'video_consultation_preview_page_view',
            id: 'video_consultation_preview_page',
            value: permissions === 'none' ? null : permissions,
            context_id: contextId,
        });
    }, [emitEvent, contextId]);
    const trackViewConsultationError = useCallback((error) => {
        emitEvent({
            component_name: 'VideoConsultationErrorPage',
            event_name: 'video_consultation_error_page_view',
            id: 'video_consultation_error_page',
            value: error,
            context_id: contextId,
        });
    }, [emitEvent, contextId]);
    const trackJoinConsultation = useCallback((permissions) => {
        emitEvent({
            component_name: 'JoinConsultationButton',
            event_name: 'join_consultation_button_click',
            id: 'join_consultation_btn',
            value: permissions,
            context_id: contextId,
        });
    }, [emitEvent, contextId]);
    const trackToggleMediaControl = useCallback((media, value) => {
        const { event_name, id } = MEDIA_EVENT_MAP[media];
        emitEvent({
            component_name: 'ConsultationMediaControl',
            event_name,
            id,
            value,
            context_id: contextId,
        });
    }, [emitEvent, contextId]);
    const didTrackSitePermissionsCheck = useRef(false);
    const trackSitePermissionsCheck = useCallback((permissionLabel) => {
        if (didTrackSitePermissionsCheck.current) {
            return;
        }
        emitEvent({
            component_name: 'VideoConsultationPermissionsCheck',
            event_name: 'video_consultation_permissions_check_ran',
            id: 'video_consultation_permissions_check',
            value: permissionLabel,
            context_id: contextId,
        });
        didTrackSitePermissionsCheck.current = true;
    }, [emitEvent, contextId]);
    const didTrackSitePermissionsPage = useRef(false);
    const trackSitePermissionsPage = useCallback(() => {
        if (didTrackSitePermissionsPage.current) {
            return;
        }
        emitEvent({
            component_name: 'VideoConsultationPermissionsPage',
            event_name: 'video_consultation_permissions_page_view',
            id: 'video_consultation_permissions_page',
            context_id: contextId,
        });
        didTrackSitePermissionsPage.current = true;
    }, [emitEvent, contextId]);
    const trackSitePermissionsPageAcknowledgement = useCallback(() => {
        emitEvent({
            component_name: 'VideoConsultationPermissionsAcknowledgeButton',
            event_name: 'video_consultation_permissions_acknowledge_button_click',
            id: 'video_consultation_permissions_acknowledge_btn',
            context_id: contextId,
        });
    }, [emitEvent, contextId]);
    const getCameraWarningContext = (warning) => {
        let contextId = 'step_1';
        switch (warning) {
            case DeviceCheckWarning.CameraPermissionPrompt:
                contextId = contextId + '-no_camera_permission';
                break;
            case DeviceCheckWarning.CameraPermissionDenied:
                contextId = contextId + '-camera_permission_denied';
                break;
            case DeviceCheckWarning.VideoInputCheckFailed:
                contextId = contextId + '-camera_detected';
                break;
            case DeviceCheckWarning.VideoInputSelectFailed:
                contextId = contextId + '-no_camera_detected';
                break;
            case null:
                contextId = contextId + '-camera_detected';
                break;
        }
        return contextId;
    };
    const didTrackCameraCheckPage = useRef(false);
    const trackCameraCheckPage = useCallback((warning, permissionLabel) => {
        if (!didTrackCameraCheckPage.current) {
            const event = {
                component_name: 'CameraHardwareCheckPage',
                event_name: 'camera_hardware_check_page_view',
                id: 'camera_hardware_check_page',
                value: permissionLabel,
                context_id: getCameraWarningContext(warning),
            };
            emitEvent(event);
            didTrackCameraCheckPage.current = true;
        }
    }, [emitEvent]);
    const trackCameraCheckFailed = useCallback((warning) => {
        emitEvent({
            component_name: 'CameraHardwareCheckButton',
            event_name: 'camera_hardware_check_button_click',
            id: 'camera_hardware_check_btn',
            value: 'continue_without_camera',
            context_id: getCameraWarningContext(warning),
        });
    }, [emitEvent]);
    const trackCameraCheckRefresh = useCallback(() => {
        emitEvent({
            component_name: 'CameraHardwareCheckButton',
            event_name: 'camera_hardware_check_button_click',
            id: 'camera_hardware_check_btn',
            value: 'refresh_screen',
            context_id: 'step_1-no_camera_detected',
        });
    }, [emitEvent]);
    const trackCameraCheckRepromptPermissions = useCallback(() => {
        emitEvent({
            component_name: 'CameraHardwareCheckButton',
            event_name: 'camera_hardware_check_button_click',
            id: 'camera_hardware_check_btn',
            value: 'ask_browser_for_permissions',
            context_id: 'step_1-no_camera_permission',
        });
    }, [emitEvent]);
    const trackCameraCheckSuccess = useCallback(() => {
        emitEvent({
            component_name: 'CameraHardwareCheckButton',
            event_name: 'camera_hardware_check_button_click',
            id: 'camera_hardware_check_btn',
            value: 'yes',
            context_id: 'step_1-camera_detected',
        });
    }, [emitEvent]);
    const didTrackMicrophoneCheckPage = useRef(false);
    const trackMicrophoneCheckPage = useCallback((permissionLabel) => {
        if (didTrackMicrophoneCheckPage.current) {
            return;
        }
        emitEvent({
            component_name: 'MicrophoneHardwareCheckPage',
            event_name: 'microphone_hardware_check_page_view',
            id: 'microphone_hardware_check_page',
            value: permissionLabel,
            context_id: 'step_2',
        });
        didTrackMicrophoneCheckPage.current = true;
    }, [emitEvent]);
    const trackMicrophoneCheckSuccess = useCallback(() => {
        emitEvent({
            component_name: 'MicrophoneHardwareCheckButton',
            event_name: 'microphone_hardware_check_button_click',
            id: 'microphone_hardware_check_btn',
            value: 'yes',
            context_id: 'step_2',
        });
    }, [emitEvent]);
    const trackMicrophoneCheckFailure = useCallback(() => {
        emitEvent({
            component_name: 'MicrophoneHardwareCheckButton',
            event_name: 'microphone_hardware_check_button_click',
            id: 'microphone_hardware_check_btn',
            value: 'no',
            context_id: 'step_2',
        });
    }, [emitEvent]);
    const didTrackSpeakerCheckPage = useRef(false);
    const trackSpeakerCheckPage = useCallback((permissionLabel) => {
        if (didTrackSpeakerCheckPage.current) {
            return;
        }
        emitEvent({
            component_name: 'SpeakerHardwareCheckPage',
            event_name: 'speaker_hardware_check_page_view',
            id: 'speaker_hardware_check_page',
            value: permissionLabel,
            context_id: 'step_3',
        });
        didTrackSpeakerCheckPage.current = true;
    }, [emitEvent]);
    const trackSpeakerCheckSuccess = useCallback(() => {
        emitEvent({
            component_name: 'SpeakerHardwareCheckButton',
            event_name: 'speaker_hardware_check_button_click',
            id: 'speaker_hardware_check_btn',
            value: 'yes',
            context_id: 'step_3',
        });
    }, [emitEvent]);
    const trackSpeakerCheckFailure = useCallback(() => {
        emitEvent({
            component_name: 'SpeakerHardwareCheckButton',
            event_name: 'speaker_hardware_check_button_click',
            id: 'speaker_hardware_check_btn',
            value: 'no',
            context_id: 'step_3',
        });
    }, [emitEvent]);
    const didTrackDeviceReadinessFailure = useRef(false);
    const trackDeviceReadinessFailure = useCallback((error, permissionLabel) => {
        if (didTrackDeviceReadinessFailure.current) {
            return;
        }
        emitEvent({
            component_name: 'ConsultationErrorPage',
            event_name: 'consultation_error_page_view',
            id: 'consultation_error_page',
            value: permissionLabel,
            context_id: DeviceCheckErrorTrackingContext[error],
        });
        didTrackDeviceReadinessFailure.current = true;
    }, [emitEvent]);
    const trackDeviceReadinessFailureInformationClick = useCallback((error, permissionLabel) => {
        emitEvent({
            component_name: 'MoreInformationButton',
            event_name: 'more_information_button_click',
            id: 'more_information_btn',
            value: permissionLabel,
            context_id: DeviceCheckErrorTrackingContext[error],
        });
    }, [emitEvent]);
    const didTrackDeviceReadinessSuccess = useRef(false);
    const trackDeviceReadinessSuccess = useCallback((permissionLabel) => {
        if (didTrackDeviceReadinessSuccess.current) {
            return;
        }
        emitEvent({
            component_name: 'JoinConsultationPage',
            event_name: 'join_consultation_button_page_view',
            id: 'join_consultation_page',
            value: permissionLabel,
        });
        didTrackDeviceReadinessSuccess.current = true;
    }, [emitEvent]);
    const trackDeviceReadinessJoinConsultationClick = useCallback((permissionLabel) => {
        emitEvent({
            component_name: 'JoinConsultationButton',
            event_name: 'join_consultation_button_click',
            id: 'join_consultation_btn',
            value: permissionLabel,
        });
    }, [emitEvent]);
    const trackChatWidgetButtonClick = useCallback((value) => {
        emitEvent({
            component_name: 'ChatWidgetButton',
            event_name: 'chat_widget_button_click',
            id: 'chat_widget_btn',
            value,
            context_id: contextId,
        });
    }, [emitEvent, contextId]);
    return {
        trackVideoConsultationLoadingPage,
        trackVideoConsultationPreview,
        trackJoinConsultation,
        trackToggleMediaControl,
        trackViewConsultationPermission,
        trackViewConsultationError,
        trackSitePermissionsCheck,
        trackSitePermissionsPage,
        trackSitePermissionsPageAcknowledgement,
        trackCameraCheckPage,
        trackCameraCheckFailed,
        trackCameraCheckRefresh,
        trackCameraCheckRepromptPermissions,
        trackCameraCheckSuccess,
        trackMicrophoneCheckPage,
        trackMicrophoneCheckSuccess,
        trackMicrophoneCheckFailure,
        trackSpeakerCheckPage,
        trackSpeakerCheckSuccess,
        trackSpeakerCheckFailure,
        trackDeviceReadinessFailure,
        trackDeviceReadinessFailureInformationClick,
        trackDeviceReadinessSuccess,
        trackDeviceReadinessJoinConsultationClick,
        trackChatWidgetButtonClick,
    };
};
//# sourceMappingURL=data:application/json;base64,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